import { Dispatch, SetStateAction, useCallback, useContext, MouseEvent } from 'react'
import { FormattedMessage } from 'react-intl'
import { Link } from 'react-router-dom'
import { Button, Menu, MenuItem, Popover } from '@creditinfo-ui/atoms'

import { Context } from '../Wrapper'
import logo from '../../assets/logo.svg'

interface Props {
  menuOpen: boolean
  setMenuOpen: Dispatch<SetStateAction<boolean>>
  toggleMobileAccountSettings: boolean
  setToggleMobileAccountSettings: Dispatch<SetStateAction<boolean>>
  user: User | null
  users: User[]
  signIn: () => void
  signOut: () => Promise<void>
  selectUser: (userId: number) => Promise<void>
  selectLanguage: (language: string) => void
  getLink: (menuItem: string) => string | undefined
  isActive: (lang: string) => boolean
  dropdownLinks: { [key: string]: string }
}

function NavbarDesktop ({
  menuOpen,
  setMenuOpen,
  user,
  users,
  signIn,
  signOut,
  selectUser,
  selectLanguage,
  getLink,
  isActive,
  dropdownLinks,
  toggleMobileAccountSettings, setToggleMobileAccountSettings
}: Props): JSX.Element {
  const context = useContext(Context)

  const handleSignIn = useCallback((): void => {
    signIn()
  }, [signIn])

  const handleSignOut = useCallback(async (): Promise<void> => {
    await signOut()
  }, [signOut])

  const handleSelectUser = useCallback(async (userId: number): Promise<void> => {
    await selectUser(userId)
  }, [selectUser])

  const handleSelectLanguage = useCallback((lang: string, e: MouseEvent<HTMLAnchorElement>): void => {
    e.preventDefault()
    selectLanguage(lang)
  }, [selectLanguage])

  const renderDropdownMenuItems = (dropdownLinks: { [key: string]: string }): JSX.Element[] => {
    return Object.entries(dropdownLinks).map(([key, href]) => (
      <a
        className="nav-link block text-white"
        href={href}
        key={key}
      >
        <FormattedMessage id={`header.${key}`} />
      </a>
    ))
  }

  const handleMenuExpand = (): void => {
    setMenuOpen(prev => !prev)
    setToggleMobileAccountSettings(false)
  }

  return (
    <div className="container xl:px-64 mx-auto flex flex-wrap items-center justify-between ">
      <Link
        className="flex items-center 2xl:hidden"
        to="/"
      >
        <img
          alt="Creditinfo"
          className="w-[186px] h-[58px]"
          src={logo}
        />
      </Link>
      <div
        className="hidden 2xl:flex items-center"
        id="navbarContent"
      >
        <Link
          className="flex items-center hidden xl:block mr-3.5"
          to="/"
        >
          <img
            alt="Creditinfo"
            className="w-[186px] h-[58px]"
            src={logo}
          />
        </Link>
        <ul className="navbar-nav flex items-center space-x-4">
          <li className="nav-item main-item dropdown relative group">
            <span className="cursor-pointer flex items-center nav-link dropdown-nav-item">
              <FormattedMessage id="header.menuItem1" />
              <svg
                className="ml-1"
                fill="currentColor"
                height="15"
                viewBox="0 0 24 24"
                width="15"
              >
                <path
                  d="M4.29289 8.29289C4.68342 7.90237 5.31658 7.90237 5.70711 8.29289L12 14.5858L18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289C20.0976 8.68342 20.0976 9.31658 19.7071 9.70711L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L4.29289 9.70711C3.90237 9.31658 3.90237 8.68342 4.29289 8.29289Z"
                />
              </svg>
            </span>
            <div className="dropdown-menu absolute hidden">
              {renderDropdownMenuItems(dropdownLinks)}
            </div>
          </li>
          <li>
            <a
              className="nav-item main-item text-white"
              href={getLink('menuItem2')}
            >
              <FormattedMessage id="header.menuItem2" />
            </a>
          </li>
          <li>
            <a
              className="nav-item main-item text-white active"
              href={getLink('menuItem3')}
            >
              <FormattedMessage id="header.menuItem3" />
            </a>
          </li>
          <li>
            <a
              className="nav-item main-item text-white font-bold"
              href={getLink('menuItem4')}
            >
              <FormattedMessage id="header.menuItem4" />
            </a>
          </li>
        </ul>
      </div>
      <div className="account hidden 2xl:flex flex items-center">
        <span className="border-r border-gray-brand h-[35px] mx-4"> </span>
        {user
          ? (
            <Popover
              popover={(
                <Menu className="font-normal">
                  <MenuItem>
                    <Link to="/archive">
                      <FormattedMessage id="navbar.archive" />
                    </Link>
                  </MenuItem>

                  <hr className="border-gray-light" />

                  {users.map(x => (
                    <MenuItem
                      className={x.id === user.id ? 'font-bold' : ''}
                      key={x.id}
                      onClick={async () => await handleSelectUser(x.id)}
                    >
                      {x.companyName}
                    </MenuItem>
                  ))}

                  {!!users.length && <hr className="border-gray-light" />}

                  <div
                    className="flex w-100"
                    style={{ paddingLeft: '2rem' }}
                  >
                    <Button
                      className="mt-4 mb-5"
                      onClick={handleSignOut}
                      size="small"
                      variant={['primary', { isOutlined: true }]}
                    >
                      <FormattedMessage id="navbar.signout" />
                    </Button>
                  </div>
                </Menu>
            )}
            >
              <div
                className="flex items-center gap-2 hover:underline font-normal text-white cursor-pointer  underline-offset-4"
              >
                <div className="max-w-[140px]">
                  <p className="truncate">
                    {user.companyName}
                  </p>
                </div>

                <svg
                  className="ml-1"
                  fill="currentColor"
                  height="15"
                  viewBox="0 0 24 24"
                  width="15"
                >
                  <path
                    d="M4.29289 8.29289C4.68342 7.90237 5.31658 7.90237 5.70711 8.29289L12 14.5858L18.2929 8.29289C18.6834 7.90237 19.3166 7.90237 19.7071 8.29289C20.0976 8.68342 20.0976 9.31658 19.7071 9.70711L12.7071 16.7071C12.3166 17.0976 11.6834 17.0976 11.2929 16.7071L4.29289 9.70711C3.90237 9.31658 3.90237 8.68342 4.29289 8.29289Z"
                  />
                </svg>
              </div>
            </Popover>
            )
          : null}

        {user === null
          ? (
            <span
              className="font-normal text-white cursor-pointer hover:underline underline-offset-4"
              onClick={handleSignIn}
            >
              <FormattedMessage id="navbar.signin" />
            </span>
            )
          : null}
        <span className="border-r border-gray-brand h-[35px] ml-4 mr-2"> </span>
        <ul className="inline lang-picker hidden 2xl:flex">
          {context.allLanguages.map((lang: string) => (
            <li
              className={`nav-item d-flex ${isActive(lang) ? 'active' : 'border-transparent'}`}
              key={lang}
            >
              <a
                className="nav-link flex items-center  cursor-pointer uppercase"
                href="#"
                onClick={(e) => handleSelectLanguage(lang, e)}
              >
                <FormattedMessage id={`navbar.language.${lang}`} />
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="navbar-expand-wrapper flex 2xl:hidden">
        <button
          className={`navbar-expand-icon ${menuOpen ? 'expanded' : 'collapsed'}`}
          onClick={handleMenuExpand}
          type="button"
        >
          <span />
        </button>
      </div>
    </div>
  )
}

export default NavbarDesktop
